import React, { useState } from 'react';
import {
  AutoComplete,
  AutoCompleteProps,
  BoxProps,
  DropdownOverlay,
  TableEditableDropdownCell,
} from '@razorpay/blade/components';
import SearchResultsView from 'components/ui/AutoCompleteV2/components/SearchResultsView';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { ArrearComponent } from './types';
import { DeductionsComponentType, EarningsComponentType } from 'components/SalaryComponents/types';

type AutocompleteSelectiveProps = Omit<AutoCompleteProps, 'onChange' | 'value'>;

type ComponentSelectorProps<T> = AutocompleteSelectiveProps & {
  options: T[];
  selectedComponent?: ArrearComponent;
  onChange?: (item: T) => void;
  onClearSelection?: () => void;
  label?: string;
  necessityIndicator?: AutoCompleteProps['necessityIndicator'];
  width?: BoxProps['width'];
};

export const ComponentSelector = ({
  options,
  selectedComponent,
  onChange,
  onClearSelection,
  label = '',
  necessityIndicator,
  width = '380px',
  ...bladeAutoCompletePropsV2
}: ComponentSelectorProps<EarningsComponentType | DeductionsComponentType>) => {
  const value = selectedComponent?.componentId;
  const selectedComponentName = selectedComponent?.componentName;
  const [searchTerm, setSearchTerm] = useState(selectedComponentName ?? '');

  const { debouncedValue: search } = useDebouncedValue(searchTerm, 200);

  const itemToKey = (item: EarningsComponentType | DeductionsComponentType) => item.id;

  const itemToLabel = (item: EarningsComponentType | DeductionsComponentType) => item.name;

  const getSearchDropdownDescription = (
    component: EarningsComponentType | DeductionsComponentType,
  ) => {
    if (component.category === 'EARNINGS_CATEGORY') {
      return 'Earning';
    } else if (component.category === 'DEDUCTIONS_CATEGORY') {
      return 'Deduction';
    }
  };

  const itemToDescription = (item: EarningsComponentType | DeductionsComponentType) =>
    getSearchDropdownDescription(item) ?? '';

  const filteredOptions = options.filter((item) =>
    itemToLabel(item)?.toLowerCase().includes(search.toLowerCase()),
  );

  const handleOnChange: AutoCompleteProps['onInputValueChange'] = (e) => {
    const newValue = e.value ?? '';
    setSearchTerm(newValue);
    if (!newValue) {
      onClearSelection?.();
    }
  };

  const handleItemSelect = (item: EarningsComponentType | DeductionsComponentType) => {
    onChange?.(item);
  };

  const handleDropdownClose = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchTerm(selectedComponentName ?? '');
    } else {
      setSearchTerm('');
    }
  };

  return (
    <TableEditableDropdownCell onOpenChange={handleDropdownClose}>
      <AutoComplete
        label={label}
        value={value ? value : undefined}
        inputValue={searchTerm}
        onInputValueChange={handleOnChange}
        onChange={({ values }) => {
          const val = values[0];
          const selectedOption = options.find((e) => itemToKey(e) === val);
          if (selectedOption) {
            handleItemSelect(selectedOption);
          }
        }}
        filteredValues={filteredOptions.map((option) => itemToKey(option))}
        necessityIndicator={necessityIndicator}
        placeholder="Eg. Bonus, Travel Allowance etc."
        {...bladeAutoCompletePropsV2}
      />

      <DropdownOverlay zIndex={9999999} width={width}>
        <SearchResultsView
          searchItems={filteredOptions}
          itemToLabel={itemToLabel}
          itemToKey={itemToKey}
          itemToDescription={itemToDescription}
        />
      </DropdownOverlay>
    </TableEditableDropdownCell>
  );
};
